/**
 * ChangeIndicator - Tracks unsaved changes in the page editor
 *
 * Key features:
 * - Subscribes to SaveQueue events for automatic tracking
 * - Provides subscribe() for UI components to react to changes
 * - Supports immediate marking via markDirty() for instant feedback
 * - Tracks which specific blocks have unsaved changes
 *
 * Implementation: Phase 2 Task 2.1
 *
 * Usage:
 *   const changeIndicator = new ChangeIndicator()
 *   changeIndicator.subscribeToSaveQueue(saveQueue)
 *
 *   // UI subscription
 *   changeIndicator.subscribe((state) => {
 *     updateToolbarBadge(state.dirtyCount)
 *     updateBlockIndicators(state.dirtyBlockIds)
 *   })
 *
 *   // Immediate marking before save queue
 *   changeIndicator.markDirty('block-123')
 */

export class ChangeIndicator {
  constructor() {
    // Set of block IDs with unsaved changes
    this.dirtyBlockIds = new Set()

    // Set of UI subscribers
    this.subscribers = new Set()

    // Unsubscribe function for SaveQueue
    this.saveQueueUnsubscribe = null
  }

  /**
   * Subscribe to change state updates
   *
   * @param {Function} callback - Called with { dirtyCount, dirtyBlockIds, hasDirtyBlocks }
   * @returns {Function} Unsubscribe function
   */
  subscribe(callback) {
    if (typeof callback !== 'function') {
      console.error('[ChangeIndicator] subscribe requires a function callback')
      return () => {}
    }

    this.subscribers.add(callback)

    // Immediately notify with current state
    callback(this.getState())

    return () => {
      this.subscribers.delete(callback)
    }
  }

  /**
   * Subscribe to SaveQueue status events for automatic tracking
   *
   * @param {SaveQueue} saveQueue - The SaveQueue instance to subscribe to
   */
  subscribeToSaveQueue(saveQueue) {
    if (!saveQueue || typeof saveQueue.subscribeStatus !== 'function') {
      console.error('[ChangeIndicator] Invalid SaveQueue provided')
      return
    }

    // Unsubscribe from previous if exists
    if (this.saveQueueUnsubscribe) {
      this.saveQueueUnsubscribe()
    }

    this.saveQueueUnsubscribe = saveQueue.subscribeStatus((status, item, _extra) => {
      if (!item?.blockId) return

      switch (status) {
        case 'pending':
        case 'processing':
        case 'retrying':
          // Mark as dirty when pending or processing
          this.markDirty(item.blockId)
          break

        case 'completed':
          // Mark as clean when successfully saved
          this.markClean(item.blockId)
          break

        case 'failed':
        case 'failed_validation':
        case 'failed_auth':
          // Keep dirty on failure - user needs to know save didn't work
          // Item stays dirty until next successful save
          break

        case 'conflict':
          // Keep dirty on conflict - needs user resolution
          break

        default:
          // Unknown status, don't change anything
          break
      }
    })

    console.log('[ChangeIndicator] Subscribed to SaveQueue')
  }

  /**
   * Mark a block as having unsaved changes
   * Call this immediately on edit for instant UI feedback
   *
   * @param {string} blockId - The block ID to mark as dirty
   */
  markDirty(blockId) {
    if (!blockId) return

    // PERF FIX: Only notify on actual state transition (clean → dirty)
    const wasAlreadyDirty = this.dirtyBlockIds.has(blockId)
    this.dirtyBlockIds.add(blockId)

    if (!wasAlreadyDirty) {
      // State actually changed - notify with transition info for incremental updates
      this.notifySubscribers({ changedBlockId: blockId, transition: 'dirty' })
    }
    // If already dirty, skip notification - no state change occurred
  }

  /**
   * Mark a block as saved (no longer dirty)
   *
   * @param {string} blockId - The block ID to mark as clean
   */
  markClean(blockId) {
    if (!blockId) return

    const hadBlock = this.dirtyBlockIds.has(blockId)
    this.dirtyBlockIds.delete(blockId)

    if (hadBlock) {
      // PERF FIX: Include transition info for incremental updates
      this.notifySubscribers({ changedBlockId: blockId, transition: 'clean' })
    }
  }

  /**
   * Check if a specific block has unsaved changes
   *
   * @param {string} blockId - The block ID to check
   * @returns {boolean} True if block has unsaved changes
   */
  isDirty(blockId) {
    return this.dirtyBlockIds.has(blockId)
  }

  /**
   * Check if any blocks have unsaved changes
   *
   * @returns {boolean} True if any blocks are dirty
   */
  hasDirtyBlocks() {
    return this.dirtyBlockIds.size > 0
  }

  /**
   * Get the current change state
   *
   * @returns {Object} { dirtyCount, dirtyBlockIds, hasDirtyBlocks }
   */
  getState() {
    return {
      dirtyCount: this.dirtyBlockIds.size,
      dirtyBlockIds: Array.from(this.dirtyBlockIds),
      hasDirtyBlocks: this.dirtyBlockIds.size > 0
    }
  }

  /**
   * Clear all dirty state (use with caution)
   * Typically called after a full page save or when discarding changes
   */
  clearAll() {
    if (this.dirtyBlockIds.size === 0) return

    this.dirtyBlockIds.clear()
    // PERF FIX: Signal that all indicators need to be cleared (full sweep needed)
    this.notifySubscribers({ changedBlockId: null, transition: 'clearAll' })
  }

  /**
   * Notify all subscribers of state change
   * @private
   * @param {Object} transition - Optional transition info for incremental updates
   * @param {string} transition.changedBlockId - The block ID that changed
   * @param {string} transition.transition - 'dirty' or 'clean'
   */
  notifySubscribers(transition = null) {
    const state = this.getState()

    // PERF FIX: Include transition info so subscribers can update incrementally
    if (transition) {
      state.changedBlockId = transition.changedBlockId
      state.transition = transition.transition
    }

    for (const subscriber of this.subscribers) {
      try {
        subscriber(state)
      } catch (error) {
        console.error('[ChangeIndicator] Subscriber error:', error)
      }
    }
  }

  /**
   * Cleanup and unsubscribe from everything
   */
  destroy() {
    if (this.saveQueueUnsubscribe) {
      this.saveQueueUnsubscribe()
      this.saveQueueUnsubscribe = null
    }

    this.subscribers.clear()
    this.dirtyBlockIds.clear()
  }
}
